/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V.
 * Gegenereerd met Junie (AI), 03-06-2026
 *
 * Angular Island voor de modelbouw-canvas. Heeft sinds golf 4d de canvas-UI van de
 * AngularJS-controller `cytocanvas.modelview` overgenomen: de tikken, het einde van een sleep en het
 * verwijderen van een relatie. Bevat naast de Cytoscape-initialisatie een statische `@for`-laag met
 * node-componenten (`dl-node-prullenbak`, `dl-node-menu`, `dl-node-label`, de hovergroep) die elk via
 * `[dlNodeVolger]` meebewegen met hun node.
 *
 * Het component is tevens de "baas" voor die node-componenten: het exposeert globale state via signals
 * en model-acties via publieke methodes, die naar de samenwerk-baas en de ng21-services gaan.
 */
import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    signal,
    computed,
    untracked,
    Signal,
    WritableSignal
} from '@angular/core';
import type {Core, CytoscapeOptions, EdgeSingular, NodeCollection, NodeSingular, EventObject, Position} from 'cytoscape';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {DlCytoCanvasBaseDirective} from '../dl-cytocanvas-base/dl-cytocanvas-base.directive';
import type {CytoCanvasView} from '../../../app/canvas/canvasregistratie.service';
import type {FeedbackMelding} from '../../nodecomponents/base-feedback-icon.component';
import {CondtagService} from '../../../services/condtag.service';
import {FeedbackbalkService} from '../../../services/feedbackbalk.service';
import {VideoStatusService} from '../../../services/video-status.service';
import {GlobalModelcanvasService} from '../../../app/model/global-modelcanvas.service';
import {SimfeedbackEngineService} from '../../../app/engines/simfeedback-engine.service';
import {NormService} from '../../../app/engines/norm.service';
import {DlModel} from '../../../app/model/dl-model';
import type {Ballon, BallonWeergave} from '../../../app/model/dl-model';
import {SessieService} from '../../../services/sessie.service';
import {OpslagService, type Modeltekenaar, type Tekenaanleiding} from '../../../app/model/opslag.service';
import {ModelbeheerService} from '../../../app/model/modelbeheer.service';
import type {ModelDocument} from '../../../app/model/model-serialisatie';
import {specialenaam} from '../../../lib/specialenaam';
import {ProjectVlaggenService} from '../../../services/project-vlaggen.service';
import {SimulatieStatusService} from '../../../services/simulatie-status.service';
import {FeedbackSelectieService} from '../../../services/feedback-selectie.service';
import {DlNodePrullenbakComponent} from '../../nodecomponents/dl-node-prullenbak/dl-node-prullenbak.component';
import {DlNodeMenuComponent} from '../../nodecomponents/dl-node-menu/dl-node-menu.component';
import {DlNodeLabelComponent} from '../../nodecomponents/dl-node-label/dl-node-label.component';
import {DlModelnodeHovergroepComponent} from '../../nodecomponents/dl-modelnode-hovergroep/dl-modelnode-hovergroep.component';
import {DlNodeMenuUitlegComponent} from '../../nodecomponents/dl-node-menu-uitleg/dl-node-menu-uitleg.component';
import {DlBallonComponent} from '../../nodecomponents/dl-ballon/dl-ballon.component';
import {elements} from 'dynalearn-common/lib/elements';
import {KeyedTimeout} from 'sihw-ngx';
import {cytoModelStyle} from './model-style';
import {SamenwerkprotocolService} from '../../../app/samenwerking/samenwerkprotocol.service';

/**
 * Een lopende add-actie: wat de gebruiker gekozen heeft en waar hij op wacht. Een relatie wacht op
 * een tweede node, een gewoon element op een klik in het canvas.
 */
export interface Addactie {
    /** Het elementtype dat geplaatst of gelegd wordt. */
    type: string;
    /** De actiedata van de menuknop; hier zit bij een quantity space het eerste elementtype in. */
    data: Record<string, unknown>;
    /** De bronnode, of `null` bij een plaatsing vanuit de toolbox. */
    node: NodeSingular | null;
    /** De toolboxknop die hem startte; een tweede klik erop annuleert. */
    knop: HTMLElement | null;
    /** Of dit een relatie is: dan wacht hij op een tweede node. */
    relatie: boolean;
    /** De nodes die als tweede argument mogen dienen; alleen bij een relatie. */
    argNodes: NodeCollection | null;
}

/**
 * Wat de automove bij het einde van een sleep meegeeft: de node die gesleept werd en wat er meebewoog.
 */
interface Automove {
    mainnode?: NodeSingular;
    movegroup?: NodeCollection;
}

/**
 * Hoe lang `rondBewerkingAf` hoogstens wacht op de opslagronde van de vastgelegde invoer. Dezelfde
 * limiet als bij de start van een replay: loopt hij af, dan gaat het gebaar gewoon door.
 */
const BEWERKING_WACHTLIMIET_MS = 3000;

@Component({
    selector: 'dl-modelcanvas',
    standalone: true,
    imports: [DlNodePrullenbakComponent, DlNodeMenuComponent, DlNodeLabelComponent, DlModelnodeHovergroepComponent, DlNodeMenuUitlegComponent, DlBallonComponent, TranslatePipe],
    templateUrl: './dl-modelcanvas.component.html',
    styleUrls: [
        '../dl-cytocanvas-base/dl-cytocanvas-base.scss',
        './dl-modelcanvas.component.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DlModelCanvasComponent extends DlCytoCanvasBaseDirective implements Modeltekenaar {

    constructor() {
        super();

        /**
         * De toon-vertraging van de emptystate: pas tonen als `magEmptystate` een halve
         * seconde onafgebroken waar is; verbergen gebeurt direct.
         */
        effect(() => {
            if (this.magEmptystate()) {
                this.timers.set('emptystate', () => this.toonEmptystate.set(true), 500);
            } else {
                this.timers.clear('emptystate');
                this.toonEmptystate.set(false);
            }
        });

        //Het effect dat de eerste normronde na het laden apart blokkeerde is met de herbouw van de
        //"is nieuw"-bepaling vervallen. Dat was een klok voor een vraag die de data zelf beantwoordt:
        //na een `reset()` heeft de NormService geen voorganger, en tegen niets is niets nieuw.

        //een samenwerkbericht zette de eerste elementen op een leeg canvas: één keer op maat brengen
        this.samenwerkprotocol.bijEersteElementen(() => {
            this.fitToMaxZoom(3);
            this.cy?.reset();
        });

        //posities die het hele model verplaatsen (snapNorm) brengen het beeld mee — bij de uitvoerder én
        //bij wie ze van de leider krijgt (testronde 4d, test 13). Een gewone sleep zet de vlag niet
        this.dlModel.bijPositiesGewijzigd(fit => {
            if (fit) this.brengBeeldOpMaat();
        });

        //een wissel van conditieset maakt een lopende add-actie ongeldig: wat je aan het plaatsen
        //was hoort in de set waarin je hem koos. In AngularJS deed elke condtag-functie dat zelf.
        //Het annuleren moet `untracked`: `annuleerAddActie` leest zelf `addActie`, en zonder dat
        //zou dit effect ook op die signal luisteren en elke add-actie meteen na het starten weer
        //afbreken.
        effect(() => {
            this.condtagService.condtag();
            untracked(() => this.annuleerAddActie());
        });

        //een nieuw of ander model: wat er geselecteerd stond of geplaatst werd hoort bij het vorige.
        //Tot golf 4c deed de ng1-hoofdcontroller dit in zijn `resetState()`, vóór elk nieuw model. Een
        //open ballon-editor hoort er ook bij, en die sluit ook bij elke hertekening (undo, redo, herladen):
        //zijn node wordt dan opnieuw aangemaakt
        this.opslag.bijGereset(() => {
            this.wisSelectie();
            this.ballonEdit.set(null);
        });
        this.opslag.bijHerladen(() => this.ballonEdit.set(null));

        //de node van de open ballon-editor is weg (verwijderd, door een ander of met undo): editor dicht
        effect(() => {
            if (this.ballonEdit() && !this.ballonEditNode()) {
                untracked(() => this.ballonEdit.set(null));
            }
        });
    }

    /**
     * Lijst van nodes die door de overlay-componenten gevolgd worden.
     * Wordt bijgewerkt op `add`/`remove`-events van het canvas. Als signal,
     * zodat een wijziging buiten Angular (cytoscape draait outside zone) in
     * de zoneless setup tóch een change-detection-cycle triggert. Zonder
     * signal verschijnt een net toegevoegd component pas bij de volgende
     * gebruikersinteractie.
     */
    protected readonly nodes: WritableSignal<NodeSingular[]> = signal([]);

    /**
     * Mag er in dit model gewerkt worden? Leest `DlModel`, die de bewerkblokkade (replay) combineert
     * met de verbindingsstatus. Eerder stond hier een eigen signal
     * dat door de modelview-bridge gevoed zou worden; dat gebeurde nooit, waardoor de guards die
     * erop leunen — verwijderen, de prullenbak, de naam — altijd openstonden, ook zonder verbinding
     * (bevinding a6.15, 02-09-2026). Er is maar één bron voor deze vraag en dat is `DlModel`.
     */
    readonly fullReadonly: Signal<boolean> = computed(() => this.dlModel.fullReadonly);

    /**
     * De lopende add-actie: wat de gebruiker aan het plaatsen of verbinden is, of `null`. Sinds
     * golf a4 stap 2 canvasstate; in AngularJS was dit `currentAddAction` in de modelviewcontroller.
     */
    readonly addActie: WritableSignal<Addactie | null> = signal(null);

    /**
     * Id van de node waar de muis op dit moment boven zweeft, of `null`. Gevoed
     * door cytoscape `mouseover`/`mouseout` op nodes. Omdat cytoscape maar één
     * node tegelijk hovert, borgt dit signaal "één hover-groep tegelijk actief".
     */
    readonly gehoverdeNodeId: WritableSignal<string | null> = signal(null);

    /**
     * De uitleg die op dit moment bij een nodemenu-item hoort, of `null` als de muis nergens op een
     * item met uitleg staat. `anker` is de menulijst waaronder het uitlegblok komt te staan. Er is er
     * bewust maar één voor het hele canvas: de muis staat nooit op twee menu-items tegelijk.
     */
    protected readonly menuUitleg: WritableSignal<{ type: string; anker: HTMLElement } | null> = signal(null);

    /**
     * Afgeleide boolean: of er momenteel een add-relatie loopt. Handig voor
     * consumers die alleen de aan/uit-status nodig hebben (zoals de node-menu's).
     */
    readonly inAddRelation: Signal<boolean> = computed(() => !!this.addActie()?.relatie);

    /**
     * Het modelbeheer (golf 4c): zolang het een model laadt, is het lege canvas geen leeg model en
     * blijft de emptystate weg.
     */
    private readonly modelbeheer = inject(ModelbeheerService);

    /**
     * De afsluiters van de invoervelden op het canvas die nu openstaan (het naamveld van een node).
     * Elk veld meldt zich aan bij het openen en af bij het sluiten; `rondBewerkingAf` roept ze aan.
     */
    private readonly openBewerkingen = new Set<() => void>();

    /**
     * De id's van de nodes waarvan het naamveld in deze selectie is geopend. Het nodemenu en de
     * prullenbak van zo'n node blijven weg tot een klik op het lichaam van de node, of tot de node
     * gedeselecteerd is. Een signal, zodat de templates van het menu en de prullenbak meelopen.
     */
    private readonly nodesMetBewerktLabel = signal<ReadonlySet<string>>(new Set());

    /**
     * Het id van de node waarvan zojuist het label is aangeklikt: het naamveld van die node hoort open te
     * gaan zodra hij geselecteerd is. Het label (`dl-node-label`) neemt het verzoek af; elke volgende klik
     * op een node zet hem opnieuw.
     */
    private readonly labelVerzoekId = signal<string | null>(null);

    /**
     * Het id van de node die als tweede argument van een relatie is aangeklikt, vanaf die klik tot de
     * relatie gelegd is. De add-actie is dan al beëindigd, maar de node is nog geen gewone selectie
     * waar een menu bij hoort.
     */
    private readonly relatieDoelId = signal<string | null>(null);

    /**
     * Of de emptystate (welkom + uitlegboxen) getoond mag worden: leeg canvas, niet aan
     * het laden en geen spelende instructievideo.
     */
    private readonly magEmptystate: Signal<boolean> = computed(() =>
        !this.modelbeheer.laden() && this.nodes().length === 0 && !this.videoStatus.speelt());

    /**
     * Of de emptystate daadwerkelijk getoond wordt: volgt `magEmptystate`, maar bij tonen
     * pas nadat die een halve seconde onafgebroken waar is (verbergen is wel direct). Zo
     * flitst de emptystate niet op in de korte lege vensters rond het laden van een model
     * of de gedebouncete `nodes`-verversing. Zie het effect in de constructor.
     */
    protected readonly toonEmptystate: WritableSignal<boolean> = signal(false);

    /**
     * De ballon die nu bewerkt wordt: de node en de tekst waarmee het tekstvak begint, of `null`. Er is er
     * hoogstens één tegelijk. Geopend vanuit het nodemenu (`uitvoerEditBallon`).
     */
    protected readonly ballonEdit: WritableSignal<{nodeId: string; tekst: string} | null> = signal(null);

    /**
     * De ballonnen van het model, om te tonen. `DlModel.ballonnen()` leest cytoscape; hij wordt opnieuw
     * gelezen als er een node bij komt of weggaat, en als de ballonnen zelf wijzigen of de graaf opnieuw
     * getekend wordt (`ballonversie`).
     */
    protected readonly ballonnen: Signal<BallonWeergave[]> = computed(() => {
        this.nodes();
        this.dlModel.ballonversie();
        return this.globalModelcanvas.getCy() ? this.dlModel.ballonnen() : [];
    });

    /**
     * De node van de ballon die bewerkt wordt, zolang hij er is.
     */
    protected readonly ballonEditNode: Signal<NodeSingular | null> = computed(() => {
        const edit = this.ballonEdit();
        this.nodes();
        return edit ? this.nodeVoor(edit.nodeId) : null;
    });

    /**
     * De sessie: de naam en het domein van de gebruiker gaan als auteur mee met een ballon.
     */
    private readonly sessie = inject(SessieService);

    /**
     * De condtag-sessie-state; sinds golf e4 de bron voor `getCondtag()`.
     */
    private readonly condtagService = inject(CondtagService);

    /**
     * Permanente eigenaar van de model-`cy`-referentie. Dit canvas meldt zijn `cy` hier aan zodat
     * `DlModel` (en de query-API daarop) hem vinden zonder dit component te kennen.
     */
    private readonly globalModelcanvas = inject(GlobalModelcanvasService);

    /**
     * De simfeedback-engine (golf 8a): bron van de per-node simfeedback-meldingen.
     */
    private readonly simfeedbackEngine = inject(SimfeedbackEngineService);

    /**
     * De norm-service (golf 3b): bron van de per-node normfeedback-meldingen.
     */
    private readonly normService = inject(NormService);

    /**
     * Het modeldocument: de vier toon-vlaggen achter de feedback-gating (golf f1).
     */
    private readonly dlModel = inject(DlModel);

    /**
     * De opslagketen (golf a3): dit eiland is er de tekenaar van, en het houdt tijdens het opbouwen
     * van een graaf het opslaan tegen.
     */
    private readonly opslag = inject(OpslagService);

    /**
     * De projectvlaggen achter de bouwsuggestie-gating (golf f1).
     */
    private readonly projectVlaggen = inject(ProjectVlaggenService);

    /**
     * Draait de simulatie? Onderdeel van de feedback-gating (golf f1).
     */
    private readonly simulatieStatus = inject(SimulatieStatusService);

    /**
     * De videostatus: een spelende instructievideo onderdrukt de emptystate (golf 3v).
     */
    private readonly videoStatus = inject(VideoStatusService);

    /**
     * De samenwerk-baas (golf a4): elke modelactie van dit canvas loopt hierlangs, die kiest tussen
     * zelf uitvoeren en het als verzoek naar de leider sturen.
     */
    private readonly samenwerkprotocol = inject(SamenwerkprotocolService);

    /**
     * De feedbackbalk: bij het herladen van een model gaan zijn popups en onthouden tellerstanden
     * op nul en staat de init-lock aan, zodat de eerste waarden na het laden geen melding opleveren.
     * Een tik op het canvas sluit zijn popupteksten.
     */
    private readonly feedbackbalk = inject(FeedbackbalkService);

    /**
     * De aanwijzing van een fout, een suggestie of een simfeedback-lus: een tik op het canvas trekt hem
     * in (golf 4d).
     */
    private readonly feedbackSelectie = inject(FeedbackSelectieService);

    /**
     * De namen uit het normmodel met hun aantal, voor de keuzelijst bij het benoemen van een node; `null`
     * als er geen norm met namen is, want dan is de naam vrij in te vullen.
     */
    private readonly normNamen: Signal<Array<{naam: string; aantal: number}> | null> = computed(() => {
        const namen = this.dlModel.norm ? this.dlModel.norm.namen : null;
        if (!namen || !Object.keys(namen).length) {
            return null;
        }
        return Object.keys(namen).map(naam => ({naam, aantal: namen[naam]}));
    });

    /**
     * Voor de vertaling van speciale namen (`___…`) in de canvaslabels.
     */
    private readonly translate = inject(TranslateService);

    /**
     * De debounce-timers van de canvas-events: de nodes-refresh ('ververs') en de
     * hoverblokkade tijdens viewport-bewegingen ('hoverblokkade').
     */
    private readonly timers = new KeyedTimeout();

    /**
     * Het aantal redenen om de icoon-animaties te onderdrukken. Er zijn er twee, en ze overlappen:
     * het tekenen van een document tot de bouwsuggestie-run klaar is. Een teller in plaats van een
     * boolean, zodat een geneste blokkade de ander niet vroegtijdig loslaat. Geen signal: hij wordt
     * alleen op het detectiemoment gelezen.
     *
     * Sinds de herbouw van de "is nieuw"-bepaling is dit een vangnet en geen dragende constructie
     * meer: wat er bij een uitslag nieuw is staat in de lijst zelf, en bij een hertekening van
     * hetzelfde model blijven de icooncomponenten staan (`track n.id()`). De blokkade die de eerste
     * normronde apart afdekte is vervallen.
     */
    private animatielock = 0;

    /**
     * View-naam waaronder dit eiland zich registreert bij de `CanvasregistratieService`.
     */
    protected readonly viewKey: CytoCanvasView = 'model';

    /**
     * Overschrijf de ngOnInit van de base om ook de modelview-events te koppelen, de `cy` aan te
     * melden bij de permanente `GlobalModelcanvasService` en ons als tekenaar aan te melden bij de
     * opslagketen.
     */
    override ngOnInit(): void {
        super.ngOnInit();
        if (this.cyInstance) {
            this.globalModelcanvas.setCy(this.cyInstance);
        }
        this.globalModelcanvas.setEiland(this);
        this.opslag.registreerTekenaar(this);
        this.initModelEvents();
    }

    /**
     * Ruimt bij destroy de `cy`-referentie in de `GlobalModelcanvasService` en de
     * tekenaar-registratie op (naast de opruiming in de base).
     */
    override ngOnDestroy(): void {
        this.timers.clearAll();
        this.opslag.deregistreerTekenaar(this);
        this.globalModelcanvas.clearEiland(this);
        if (this.cyInstance) {
            this.globalModelcanvas.clearCy(this.cyInstance);
        }
        super.ngOnDestroy();
    }

    /**
     * Controleert of de opgegeven node verwijderd mag worden. Volledig in ng21
     * berekend, analoog aan `magNodeLabelBewerken`: element-definitie
     * (`deletable`), `fullReadonly`, condtag-filtering, de add-relatie-uitzondering en de
     * type-specifieke check voor quantity-space-punten en -intervallen (`magTypeWeg`).
     */
    magNodeDeleten(node: NodeSingular): boolean {
        if (this.isSimulatienode(node)) return false;
        if (!elements.getDefinition(node.data('type'))?.deletable) return false;
        if (this.fullReadonly()) return false;
        if (!this.nodeInCondtag(node)) return false;
        if (this.inAddRelation()) return false;
        return this.magTypeWeg(node);
    }

    /**
     * De type-specifieke deletecheck: een quantity-space-punt of -interval mag alleen weg als het de
     * bovenste of de onderste is en er één overblijft. In AngularJS was dit de losse
     * `$scope.checkDelete_<nodetype>`-hook; er is er maar één van.
     */
    private magTypeWeg(node: NodeSingular): boolean {
        const type = node.data('type') as string;
        if (type !== 'quantity_space_point' && type !== 'quantity_space_interval') {
            return true;
        }
        return this.dlModel.magQsElementWeg(node);
    }

    /**
     * Verwijdert de opgegeven node.
     */
    deleteNode(node: NodeSingular): void {
        this.samenwerkprotocol.doe('delete', {node});
    }

    /**
     * Bepaalt of de opgegeven node via de prullenbak verwijderd mag worden. Vertakt: een
     * value-/ineq-history-container (`simhistory_container`, golf 5d/5e) heeft een eigen sim-tak —
     * het is geen elementtype, dus alleen `fullReadonly` blokkeert. De rest loopt via `magNodeDeleten`.
     */
    magNodeVerwijderen(node: NodeSingular): boolean {
        if (this.isSimhistoryContainer(node)) {
            // een door ng21 beheerde value-/ineq-history-container draagt de verbergroute als scratch;
            // die claimt de prullenbak, mits niet volledig readonly.
            return !this.fullReadonly() && typeof node.scratch('simhistoryVerberg') === 'function';
        }
        return this.magNodeDeleten(node);
    }

    /**
     * Verwijdert de opgegeven node via de prullenbak. Voor een history-container loopt dit via de
     * `simhistoryVerberg`-scratch (de verbergroute van het simcanvas) i.p.v. `deleteNode`/`lokaalDelete`: het
     * verbergen van een history is geen modelmutatie.
     */
    verwijderNode(node: NodeSingular): void {
        if (this.isSimhistoryContainer(node)) {
            const verberg = node.scratch('simhistoryVerberg');
            if (typeof verberg === 'function') {
                verberg();
            }
            return;
        }
        this.deleteNode(node);
    }

    /**
     * Of de node door de simulatie op het modelcanvas getekend is (class `simulation`): waarden,
     * afgeleide waarden, ineqs en de history-onderdelen. Zulke nodes zijn simulatie-transiënt en
     * krijgen geen node-UI — ze zijn geen modelelement, dus verwijderen, hernoemen of van type
     * wisselen slaat nergens op. In ng1 volgde dat uit de `!node.hasClass('simulation')`-conditie
     * rond `createNodeMenu`, die daar prullenbak, typemenu én labelinvoer aanmaakte; sinds de
     * node-componenten zelf hun zichtbaarheid bepalen staat de regel hier. De
     * value-/ineq-history-container is de uitzondering: die claimt zijn eigen prullenbak (de sim-tak
     * van `magNodeVerwijderen`, golf 5d/5e) en komt hier dus niet langs.
     */
    isSimulatienode(node: NodeSingular): boolean {
        return node.hasClass('simulation');
    }

    /**
     * Of de node een value-/ineq-history-container is (de sim-tak van de prullenbak-gating).
     */
    private isSimhistoryContainer(node: NodeSingular): boolean {
        return node.data('type') === 'simhistory_container';
    }

    /**
     * Vervangt het type van de opgegeven node. Sluit de lopende add-actie en de selectie: het menu
     * waar dit uit komt hoort daarna dicht te zijn.
     */
    uitvoerChangeAction(node: NodeSingular, actiedata: Record<string, unknown>): void {
        this.annuleerAddActie();
        this.samenwerkprotocol.doe('changeNode', {node, type: actiedata['type']});
        this.wisSelectie();
    }

    /**
     * Initieert een add-actie vanaf een node: het toevoegen van een relatie of een subelement. Een
     * tweede klik op dezelfde knop annuleert (toggle). Bij een relatie worden de nodes gezocht die
     * als tweede argument kunnen dienen; is er geen enkele, dan gaat de actie niet door.
     */
    uitvoerInitAddAction(node: NodeSingular, actiedata: Record<string, unknown>): void {
        this.videoStatus.cancel();
        const type = actiedata['type'] as string;
        const vorige = this.addActie();
        this.annuleerAddActie();
        if (vorige?.type === type && vorige.node?.id() === node.id()) {
            return; //dezelfde knop nogmaals: het annuleren hierboven was de hele actie
        }
        if (!elements.isSubtypeOf(type, 'relation')) {
            this.addActie.set({type, data: actiedata, node, knop: null, relatie: false, argNodes: null});
            return;
        }
        const argNodes = this.mogelijkeArgumenten(node, type);
        if (!argNodes?.length) {
            return; //er is niets om deze relatie mee te leggen
        }
        this.markeerArgumenten(argNodes);
        this.addActie.set({type, data: actiedata, node, knop: null, relatie: true, argNodes});
    }

    /**
     * Start een add-actie vanuit de tekentools van het bouwscherm (entity, agent, assumption). Er is geen
     * bronnode; de klik op het canvas bepaalt de positie. Een tweede klik op dezelfde knop
     * annuleert. Relaties lopen nooit langs hier — die komen uit een nodemenu.
     */
    startAddActie(type: string, data: Record<string, unknown>, knop: HTMLElement | null): void {
        const vorige = this.addActie();
        this.annuleerAddActie();
        if (vorige?.knop && vorige.knop === knop) {
            return;
        }
        if (elements.isSubtypeOf(type, 'relation')) {
            return;
        }
        knop?.classList.add('selectedMenuItem');
        this.addActie.set({type, data, node: null, knop, relatie: false, argNodes: null});
    }

    /**
     * Breekt de lopende add-actie af en zet de graaf terug in zijn gewone stand.
     */
    annuleerAddActie(): void {
        const actie = this.addActie();
        if (!actie) {
            return;
        }
        actie.knop?.classList.remove('selectedMenuItem');
        if (this.cy) {
            //alles weer selecteerbaar en versleepbaar als het dat was, en visueel aan
            this.cy.nodes('.addactionselectable').removeClass('addactionselectable').selectify();
            this.cy.nodes('.addactiongrabbable').removeClass('addactiongrabbable').grabify();
            this.cy.nodes('.node_disable').removeClass('node_disable');
            this.cy.edges('.edge_disable').removeClass('edge_disable');
        }
        this.addActie.set(null);
    }

    /**
     * Wisselt de argumenten van een relatie-node om.
     */
    uitvoerSwitchArgs(node: NodeSingular): void {
        this.annuleerAddActie();
        this.samenwerkprotocol.doe('switchArgs', {node});
        this.wisSelectie();
    }

    /**
     * Opent de ballon-editor voor de gegeven node, met de tekst die er al stond. De selectie gaat eraf,
     * zodat het nodemenu de editor niet afdekt. Tot golf 4d liep dit via de modelview-bridge naar een
     * AngularJS-editor.
     */
    uitvoerEditBallon(node: NodeSingular): void {
        this.wisSelectie();
        this.ballonEdit.set({nodeId: node.id(), tekst: (node.data('ballon') as Ballon | undefined)?.tekst ?? ''});
    }

    /**
     * Slaat de bewerkte ballon op als modelactie (de baas: undo-punt, log, samenwerking, opslaan). Een
     * lege tekst haalt de ballon weg. De auteur is de gebruiker; zijn domein reist mee in de logregel en
     * over de lijn, al bewaart het model alleen tekst en naam.
     * @param tekst wat er in het tekstvak stond
     */
    protected slaBallonOp(tekst: string): void {
        const edit = this.ballonEdit();
        if (!edit) {
            return;
        }
        const ballon = tekst.length
            ? {tekst, auteur: this.sessie.displaynaam(), auteur_id: this.sessie.userdomein()}
            : null;
        this.samenwerkprotocol.doe('setBallon', {ballon, nodeId: edit.nodeId});
        this.ballonEdit.set(null);
    }

    /**
     * Sluit de ballon-editor zonder op te slaan.
     */
    protected annuleerBallonEdit(): void {
        this.ballonEdit.set(null);
    }

    /**
     * Haalt een ballon weg: de docent klikte hem twee keer weg.
     * @param nodeId de node waar hij bij hoorde
     */
    protected wegklikBallon(nodeId: string): void {
        this.samenwerkprotocol.doe('setBallon', {ballon: null, nodeId});
    }

    /**
     * De node met dit id op het canvas, of `null` als hij er niet (meer) is.
     */
    protected nodeVoor(id: string): NodeSingular | null {
        const node = this.globalModelcanvas.getCy()?.$id(id);
        return node?.nonempty() ? node[0] as NodeSingular : null;
    }

    /**
     * Schakelt de allewaarden-markering bij een quantity aan of uit.
     */
    uitvoerToggleAllewaarden(node: NodeSingular): void {
        this.wisSelectie();
        this.samenwerkprotocol.doe('toggleAllewaarden', {node});
    }

    /**
     * Schakelt de waarde bij een afgeleide aan of uit.
     */
    uitvoerToggleDValue(node: NodeSingular): void {
        this.wisSelectie();
        this.samenwerkprotocol.doe('toggleDValue', {node});
    }

    /**
     * Schakelt de waarde bij een quantity-space-item aan of uit.
     */
    uitvoerToggleQValue(node: NodeSingular): void {
        this.wisSelectie();
        this.samenwerkprotocol.doe('toggleQValue', {node});
    }

    /**
     * Voegt een nieuw quantity-space-element toe, boven- of onderaan.
     */
    uitvoerNewQuantitySpaceElement(node: NodeSingular, positie: 'top' | 'bottom'): void {
        this.wisSelectie();
        this.samenwerkprotocol.doe('addQSE', {qsNode: node, positie});
    }

    /**
     * Zet of verwijdert de zero-markering van een quantity-space-punt.
     */
    uitvoerSetQsPointZero(node: NodeSingular): void {
        this.wisSelectie();
        this.samenwerkprotocol.doe('qsZero', {node});
    }

    /**
     * Schakelt een exogeen-type bij een quantity aan of uit.
     */
    uitvoerToggleExogeen(node: NodeSingular, exoType: string): void {
        this.wisSelectie();
        this.samenwerkprotocol.doe('toggleExogeen', {node, type: exoType});
    }

    /**
     * De nodes die als tweede argument van deze relatie kunnen dienen, gezien vanaf de bronnode.
     * Loopt de `possibleargs` van de definitie af: per paar staat aan één kant het type van de bron
     * en aan de andere kant wat er tegenover mag staan (`sub:`-notatie voor een hele tak).
     */
    private mogelijkeArgumenten(bron: NodeSingular, type: string): NodeCollection | null {
        if (!this.cy) {
            return null;
        }
        const bronType = bron.data('type') as string;
        const selectors: string[] = [];
        for (const paar of (elements.getDefinition(type).possibleargs ?? []) as string[][]) {
            const links = paar[0].startsWith('sub:') ? elements.alleSubs(paar[0].slice(4)) : [paar[0]];
            const rechts = paar[1].startsWith('sub:') ? elements.alleSubs(paar[1].slice(4)) : [paar[1]];
            const doelen = links.includes(bronType) ? rechts : (rechts.includes(bronType) ? links : null);
            for (const doel of doelen ?? []) {
                selectors.push(`[type = "${doel}"]`);
            }
        }
        return selectors.length ? this.cy.$(selectors.join(', ')).not(bron) : null;
    }

    /**
     * Zet de graaf in de argumentkeuze-stand: alles op non-actief behalve de nodes die het tweede
     * argument mogen zijn. De classes onthouden wat er ervoor selecteerbaar en versleepbaar was,
     * zodat `annuleerAddActie` het precies terug kan zetten.
     */
    private markeerArgumenten(argNodes: NodeCollection): void {
        if (!this.cy) {
            return;
        }
        this.cy.nodes(':grabbable').addClass('addactiongrabbable');
        this.cy.nodes(':selectable').addClass('addactionselectable');
        this.cy.nodes().addClass('node_disable').unselectify().ungrabify();
        this.cy.edges().addClass('edge_disable');
        argNodes.removeClass('node_disable').selectify();
    }

    /**
     * Handelt een tap op een node af binnen een lopende add-actie: is het een geldig tweede
     * argument, dan wordt de relatie gelegd. Elke andere tap breekt de actie af.
     */
    private checkAddActie(node: NodeSingular): void {
        const actie = this.addActie();
        if (!actie?.relatie) {
            this.annuleerAddActie();
            return;
        }
        if (actie.argNodes?.anySame(node)) {
            //een tik later: deze tap wordt nog door een select gevolgd, en die zou de nieuwe relatie
            //meteen weer deselecteren (zo deed AngularJS het ook). Vóór het leggen gaat de selectie eraf:
            //de bron en het aangeklikte argument blijven anders geselecteerd staan, met hun menu, want de
            //nieuwe relatie wordt in code geselecteerd en `selectionType: 'single'` geldt alleen voor een
            //klik van de gebruiker
            this.relatieDoelId.set(node.id());
            setTimeout(() => {
                this.cy?.$(':selected').unselect();
                this.samenwerkprotocol.doe('addRelation', {van: actie.node, naar: node, type: actie.type});
                this.relatieDoelId.set(null);
            });
            this.annuleerAddActie();
        }
    }

    /**
     * Plaatst het element van de lopende add-actie op de aangetikte positie.
     */
    private plaatsAddActie(positie: Position): void {
        const actie = this.addActie();
        if (!actie) {
            return;
        }
        this.annuleerAddActie();
        this.samenwerkprotocol.doe('addNode', {
            positie, type: actie.type, node: actie.node, data: actie.data
        });
    }

    /**
     * Deselecteert alles en breekt de add-actie af. In AngularJS `clearSingleSelection`.
     */
    wisSelectie(): void {
        this.cy?.$(':selected').unselect();
        this.annuleerAddActie();
    }

    /**
     * Rondt het bewerken op het canvas af, zoals een klik in het lege canvas dat doet: een open
     * invoerveld legt zijn waarde vast als gewone actie, en de selectie gaat eraf. Wacht daarna tot
     * de opslagronde van die actie klaar is, zodat wat hierna komt op het model mét die invoer werkt.
     *
     * Het vastleggen gebeurt hier meteen, niet via de deselectie: het naamveld sluit op een effect,
     * en dat draait pas bij de volgende change detection. Is er niets open, dan is dit alleen het
     * wissen van de selectie.
     *
     * Aangeroepen door de twee simulatieknoppen (simuleren en stap voor stap), vóór hun gebaar. Ook
     * bruikbaar voor andere momenten waarop een half ingevoerde naam niet mag blijven hangen.
     */
    async rondBewerkingAf(): Promise<void> {
        for (const sluit of this.openBewerkingen) {
            sluit();
        }
        this.wisSelectie();
        if (!await this.samenwerkprotocol.wachtOpOpslagronde(BEWERKING_WACHTLIMIET_MS)) {
            this.log.warn('de opslagronde van de afgeronde bewerking was niet op tijd klaar');
        }
    }

    /**
     * Schuift het model naar de vorm van het normmodel: een actie van de baas, dus een volger vraagt het
     * de leider. Het beeld gaat mee met de posities, bij iedereen die ze krijgt (zie het abonnement op
     * `bijPositiesGewijzigd` in de constructor); bij een volger komen ze pas later binnen. Zonder
     * normmatch valt er niets te schuiven. De knop zit in `dl-canvasknoppen`; het bouwscherm geeft zijn
     * klik via de `GlobalModelcanvasService` hierheen door.
     */
    snapNorm(): void {
        if (!this.normService.normmatch() || !this.cy) {
            return;
        }
        this.samenwerkprotocol.doe('snapNorm');
    }

    /**
     * Meldt een geopend invoerveld aan, met de functie die het vastlegt en sluit.
     */
    meldBewerkingOpen(sluit: () => void): void {
        this.openBewerkingen.add(sluit);
    }

    /**
     * Meldt een invoerveld af dat gesloten is.
     */
    meldBewerkingDicht(sluit: () => void): void {
        this.openBewerkingen.delete(sluit);
    }

    /**
     * Of het naamveld van de opgegeven node in deze selectie is geopend; zo ja, dan horen het
     * nodemenu en de prullenbak van de node weg te blijven.
     */
    isLabelBewerkt(node: NodeSingular): boolean {
        return this.nodesMetBewerktLabel().has(node.id());
    }

    /**
     * Of het nodemenu en de prullenbak van de opgegeven node getoond mogen worden. Niet tijdens het
     * leggen van een relatie: dan hoort alleen de bronnode zijn menu te houden, en de node die als
     * tweede argument wordt aangeklikt krijgt er geen, ook niet in de tik tussen de klik en het leggen
     * van de relatie.
     */
    magNodeMenuTonen(node: NodeSingular): boolean {
        if (this.relatieDoelId() === node.id()) {
            return false;
        }
        const actie = this.addActie();
        return !actie?.relatie || actie.node?.id() === node.id();
    }

    /**
     * Of een klik op deze plek het label van de node raakte en niet het lichaam: de klik viel buiten het
     * lichaam van de node, maar kwam toch bij de node binnen (de labels hebben `text-events: yes`).
     * @param node de aangeklikte node
     * @param positie de klikpositie in modelcoördinaten (`event.position`)
     */
    isLabelKlik(node: NodeSingular, positie: Position): boolean {
        const lichaam = node.boundingBox({includeLabels: false});
        return positie.x < lichaam.x1 || positie.x > lichaam.x2 || positie.y < lichaam.y1 || positie.y > lichaam.y2;
    }

    /**
     * Het id van de node waarvan het label zojuist is aangeklikt, of `null`. Een signal-lezing, dus het
     * label kan er in een effect op reageren.
     */
    labelBewerkVerzoek(): string | null {
        return this.labelVerzoekId();
    }

    /**
     * Neemt het verzoek om een naamveld te openen af.
     */
    wisLabelBewerkVerzoek(): void {
        this.labelVerzoekId.set(null);
    }

    /**
     * Legt vast dat het naamveld van de opgegeven node is geopend, of haalt dat weer weg (bij een klik
     * op het lichaam van de node, of bij het deselecteren).
     */
    zetLabelBewerkt(node: NodeSingular, bewerkt: boolean): void {
        if (this.nodesMetBewerktLabel().has(node.id()) === bewerkt) {
            return;
        }
        const nodes = new Set(this.nodesMetBewerktLabel());
        if (bewerkt) {
            nodes.add(node.id());
        } else {
            nodes.delete(node.id());
        }
        this.nodesMetBewerktLabel.set(nodes);
    }

    /**
     * Geeft het effectieve modelleerniveau van het model terug; de menu's en de uitleg hangen ervan af.
     */
    getEffectiefModelleerniveau(): number {
        return this.dlModel.effectief_modelleerniveau;
    }

    /**
     * Geeft de huidige condtag-state terug, of null als er geen actieve condtag is.
     * Sinds golf e4 uit de `CondtagService`.
     */
    getCondtag(): {tag: string; state: 'cond' | 'cons'} | null {
        return this.condtagService.condtag();
    }

    /**
     * Controleert of de naam van de opgegeven node op dit moment bewerkt mag
     * worden. Volledig in ng21 berekend: element-definitie (`hasname`),
     * `fullReadonly`, condtag-filtering, `skipName` en de add-relatie-uitzondering
     * (alleen de bron-node is dan bewerkbaar). De actieve condtag komt uit `getCondtag()`
     * (de `CondtagService`).
     */
    magNodeLabelBewerken(node: NodeSingular): boolean {
        if (!node) return false;
        if (this.isSimulatienode(node)) return false;
        if (!elements.getDefinition(node.data('type'))?.hasname) return false;
        if (this.fullReadonly()) return false;
        if (!this.nodeInCondtag(node)) return false;
        if (node.data('skipName')) return false;
        const bron = this.addActie()?.relatie ? this.addActie()?.node : null;
        return !(bron && bron.id() !== node.id());
    }

    /**
     * Of de node-overlays (hovergroep, nodemenu) voor deze node getoond mogen worden:
     * gewone nodes altijd, conditionele elementen alleen in de matchende condtag-state.
     * Arrow-property zodat de referentie als volger-input doorgegeven kan worden.
     */
    readonly nodeOverlaysZichtbaar = (node: NodeSingular): boolean =>
        !node.data('condtag') || this.nodeInCondtag(node);

    /**
     * Bepaalt of een node bij de huidige actieve condtag hoort. 1-op-1 vertaling
     * van de ng1-functie `nodeInCondtag`, met de condtag uit `getCondtag()`.
     */
    private nodeInCondtag(node: NodeSingular): boolean {
        const nodetag = node.data('condtag');
        const nodestate = node.data('condstate');
        const condtag = this.getCondtag();
        if ((!condtag && nodetag) || (condtag && !nodetag)) return false;
        if (!condtag) return true;
        return nodetag === condtag.tag && nodestate === condtag.state;
    }

    /**
     * Geeft de beschikbare normnamen terug voor de label-`select`, of `null`
     * wanneer het model geen norm met namen heeft (vrije invoer).
     */
    getNormNamen(): Array<{naam: string; aantal: number}> | null {
        return this.normNamen();
    }

    /**
     * Slaat een gewijzigde nodenaam op als modelactie van de samenwerk-baas.
     */
    uitvoerWijzigNaam(node: NodeSingular, naam: string): void {
        this.samenwerkprotocol.doe('wijzigNaam', {node, naam});
        //de graaf is leidend: ging het verzoek naar de leider, dan staat de oude naam er nog
        this.dlModel.zetNodelabel(node);
    }

    /**
     * Geeft terug of de ingrediënt-uitleg (tooltip) voor dit project aanstaat.
     */
    isUitlegAan(): boolean {
        return this.projectVlaggen.tooltipIngredient();
    }

    /**
     * Geeft terug of de uitleg bij de nodemenu-items voor dit project aanstaat.
     */
    isMenuUitlegAan(): boolean {
        return this.projectVlaggen.tooltipBouwactiviteit();
    }

    /**
     * Meldt dat de muis op een menu-item met uitleg staat. `anker` is de menulijst waar dat item in
     * zit; het uitlegblok positioneert zich daaronder. Een melding die gelijk is aan de vorige wordt
     * genegeerd: het menu meldt bij elke muisbeweging binnen hetzelfde item, en een nieuw signaal zou
     * de toonvertraging telkens opnieuw starten.
     */
    meldMenuUitleg(type: string, anker: HTMLElement): void {
        const huidig = this.menuUitleg();
        if (huidig?.type !== type || huidig.anker !== anker) {
            this.menuUitleg.set({type, anker});
        }
    }

    /**
     * Wist de menu-uitlegmelding. Met een `eigenaar` erbij wordt alleen gewist als de huidige melding
     * uit dat menu komt: een menu dat afgebroken wordt mag de uitleg van een ander menu niet weghalen.
     */
    wisMenuUitleg(eigenaar?: HTMLElement): void {
        const huidig = this.menuUitleg();
        if (huidig && (!eigenaar || eigenaar.contains(huidig.anker))) {
            this.menuUitleg.set(null);
        }
    }

    /**
     * Logt een uitleg-interactie (toon/hide/muisin/muisuit/meer) via de samenwerk-baas (`uitlegActie`).
     */
    logUitlegActie(type: string, node: NodeSingular): void {
        this.samenwerkprotocol.uitlegActie(type, node);
    }

    /**
     * Geeft terug of normfeedback aan staat (toggle aan + niet in simulatie). Sinds golf f1
     * rechtstreeks uit `DlModel` en de `SimulatieStatusService`.
     */
    isNormfeedbackAan(): boolean {
        return !this.simulatieStatus.simuleren() && this.dlModel.normToon;
    }

    /**
     * Geeft de normfeedback-meldingen voor de opgegeven node terug. Sinds golf 3b rechtstreeks uit
     * de `NormService`; de reactiviteit loopt via het foutoverzicht-signal dat
     * `getMeldingenVoorNode` leest.
     */
    getNormfeedbackVoorNode(node: NodeSingular): FeedbackMelding[] {
        return this.normService.getMeldingenVoorNode(node);
    }

    /**
     * Geeft terug of simfeedback aan staat (toggle aan + simulatie loopt). Sinds golf f1
     * rechtstreeks uit `DlModel` en de `SimulatieStatusService`.
     */
    isSimfeedbackAan(): boolean {
        return this.simulatieStatus.simuleren() && this.dlModel.simToon;
    }

    /**
     * Geeft de simfeedback-meldingen voor de opgegeven node terug. Sinds golf 8a rechtstreeks uit
     * de `SimfeedbackEngineService`; de reactiviteit loopt via het feedback-signal dat
     * `getMeldingenVoorNode` leest (geen `feedbackVersie` meer nodig voor dit deel).
     */
    getSimfeedbackVoorNode(node: NodeSingular): FeedbackMelding[] {
        return this.simfeedbackEngine.getMeldingenVoorNode(node);
    }

    /**
     * Herlaadt het model: er is nieuwe modeldata (een ander model, een herlading van hetzelfde
     * model bij samenwerking, of een undo/redo-stap) en het canvas begint daarop opnieuw. Naast het
     * tekenen zelf hoort daarbij dat er tijdens het opbouwen niet opgeslagen wordt, dat de
     * feedbackbalk zijn onthouden standen vergeet en zijn init-lock aan heeft, en dat de eerste
     * normronde van een nieuw model niet animeert.
     *
     * Dit is de tekenaar-kant van de opslagketen: `OpslagService` roept hem aan zodra de stand
     * vervangen is (sinds golf a3; daarvoor deed de ng1-`model.changed`-handler dat).
     * @param doc de nieuwe modeldata, in het opslagformaat mét `meta`
     * @param aanleiding waarom er getekend wordt
     */
    public herlaad(doc: ModelDocument, aanleiding: Tekenaanleiding): void {
        if (!this.dlModel.isVolledigDocument(doc)) {
            this.log.error('Missende data in opgeslagen graph', doc);
            return;
        }
        this.opslag.blokkeerOpslag(() => {
            this.feedbackbalk.reset();
            this.feedbackbalk.zetInitLock(true);
            void this.tekenDocument(doc, aanleiding);
        });
        // na een korte stabilisatietijd nogmaals resetten, zodat de onthouden tellerstanden de
        // actuele stabiele waarde krijgen, en dan pas de meldingen weer toelaten
        this.timers.set('initlock', () => {
            this.feedbackbalk.reset();
            this.feedbackbalk.zetInitLock(false);
        }, 300);
    }

    /**
     * Tekent een document op het canvas: de interactie-resets, de graaf zelf via
     * `DlModel.tekenGraaf`, de labels en de viewport. Het tekenen is synchroon — als de aanroep
     * terugkeert staat de graaf er en zijn de tekenmelding-luisteraars (simfeedback gewist, de
     * bouwsuggestie-run gestart) aangeroepen; alleen het loslaten van de animatieblokkade wacht op
     * die run. Aangeroepen door `herlaad`.
     * @param doc het opslagformaat
     * @param aanleiding waarom er getekend wordt. Alleen bij een ánder model wordt de zoom uit het
     *   document overgenomen, de graaf gecentreerd en alleen als hij zo niet in beeld past alsnog
     *   gefit; bij elke andere aanleiding blijft de uitsnede van de gebruiker staan
     */
    public async tekenDocument(doc: ModelDocument, aanleiding: Tekenaanleiding): Promise<void> {
        const cy = this.cy;
        if (!cy) return;
        this.blokkeerAnimaties();
        try {
            this.resetInteractie();
            //de aanleiding reist mee: de bouwsuggestie-run die hierop volgt moet weten of dit een
            //teruggehaalde stand is, en dat is hier bekend en verderop niet meer
            const klaar = this.dlModel.tekenGraaf(doc, aanleiding);
            cy.nodes('.model').forEach((node) => this.zetNodelabel(node));
            cy.resize(); // voor de zekerheid
            if (aanleiding === 'nieuw model') {
                // De volgorde telt: `cy.zoom(level)` laat de pan van het vorige model staan, en de
                // "past het nog"-check van `fitToMaxZoom` rekent met die pan. Zonder `center()`
                // ertussen ligt het nieuwe model buiten de oude uitsnede en wordt er altijd gefit,
                // waarmee de opgeslagen zoom verloren gaat (bevinding golf a2 stap 2, test 2).
                cy.zoom(doc.cy.zoom || 0.5);
                cy.center();
                this.fitToMaxZoom(3, true); // alleen als het zo niet past
            }
            await klaar;
        } catch (e) {
            this.log.error('tekenDocument mislukt', e);
        } finally {
            this.laatAnimatiesLos();
        }
    }

    /**
     * Levert een jpg van het model zoals het op het canvas staat, als base64-string (geen url).
     * Wordt bij het opslaan meegestuurd als voorbeeldplaatje van het model; de opslagketen vraagt
     * hem via de tekenaar-registratie.
     * @param maxwidth maximale breedte bij het schalen, standaard 1200
     * @param maxheight maximale hoogte bij het schalen, standaard 1200
     */
    public jpg(maxwidth?: number, maxheight?: number): string {
        return this.cy?.jpg({
            output: 'base64',
            full: true,
            maxWidth: maxwidth || 1200,
            maxHeight: maxheight || 1200
        }) ?? '';
    }

    /**
     * Meldt een logbare bouwsuggestie-actie aan de samenwerk-baas: die logt, zet zo nodig een
     * undo-punt en slaat op, en stuurt een antwoord van een volger naar de leider. De engine zelf
     * (registratie, regelgroepen, regelstatus) is sinds golf 8b de `BouwsuggestieEngineService`,
     * die `dl-bouwsuggestie` rechtstreeks injecteert.
     */
    bouwsuggestieActie(actiedata: {type: string; node: NodeSingular; wijziging?: boolean; visuelewijziging?: boolean; open?: boolean; args?: Record<string, unknown>}): void {
        this.samenwerkprotocol.bouwsuggestieActie(actiedata);
    }

    /**
     * Geeft terug of bouwsuggesties (noodzakelijk) getoond moeten worden. Sinds golf f1
     * rechtstreeks uit de projectvlaggen, `DlModel` en de `SimulatieStatusService`.
     */
    isBouwsuggestieNoodzakelijkAan(): boolean {
        return !this.simulatieStatus.simuleren() && this.projectVlaggen.bouwsuggestiesNoodzakelijk() && this.dlModel.bsToonNoodzakelijk;
    }

    /**
     * Geeft terug of bouwsuggesties (gewenst) getoond moeten worden.
     */
    isBouwsuggestieGewenstAan(): boolean {
        return !this.simulatieStatus.simuleren() && this.projectVlaggen.bouwsuggestiesGewenst() && this.dlModel.bsToonGewenst;
    }

    /**
     * Geeft terug of het project noodzakelijke bouwsuggesties ondersteunt.
     */
    projectOndersteuntBouwsuggestieNoodzakelijk(): boolean {
        return this.projectVlaggen.bouwsuggestiesNoodzakelijk();
    }

    /**
     * Geeft terug of het model op dit moment in teacher-replay wordt afgespeeld.
     */
    isInReplay(): boolean {
        return this.dlModel.inReplay();
    }

    /**
     * Geeft terug of de icoon-animaties op dit moment onderdrukt moeten worden: tijdens het laden
     * of hertekenen van een model komen alle meldingen tegelijk binnen. Zie `animatielock`.
     */
    public zijnAnimatiesGeblokkeerd(): boolean {
        return this.animatielock > 0;
    }

    /**
     * Vult de Cytoscape-opties aan met de modelbouw-specifieke stylesheet en
     * de interactie-instellingen die in de oude AngularJS-controller stonden.
     */
    protected override configureerCanvas(opties: CytoscapeOptions): CytoscapeOptions {
        return {
            ...opties,
            style: cytoModelStyle,
            zoomingEnabled: true,
            userZoomingEnabled: true,
            panningEnabled: true,
            userPanningEnabled: true,
            boxSelectionEnabled: false,
            selectionType: 'single',
            touchTapThreshold: 8,
            desktopTapThreshold: 10,
            autolock: false,
            autoungrabify: false,
            autounselectify: false,
            hideLabelsOnViewport: true,
            motionBlur: true,
            textureOnViewport: true,
            pixelRatio: 'auto'
        };
    }

    /**
     * Initialiseert de modelbouw-specifieke events op het canvas.
     */
    private initModelEvents(): void {
        const cy = this.cy;
        if (!cy) return;

        /**
         * Houd de nodes-array gesynchroniseerd met de werkelijke nodes in cy,
         * zodat de overlay-laag (prullenbak/menu/label) elke node krijgt.
         * De refresh wordt gedebouncet zodat een batch toevoegingen (bv. bij
         * laden van een model, of een `cy.add` direct gevolgd door
         * `node.select()`) leidt tot één update nadat cytoscape zijn state
         * heeft afgerond. Zo zijn nodes bij creatie van het component al in
         * hun definitieve staat (selectie, data) en hoeft niemand achteraf
         * te corrigeren.
         */
        const verversNodes = (): void => {
            this.timers.set('ververs', () => this.zetNodes(cy), 50);
        };
        // Initiële sync direct, geen debounce nodig.
        this.zetNodes(cy);
        //Het canvaslabel hoort bij het canvas: elke node die erbij komt krijgt het hier, in plaats
        //van bij elke plek die een node toevoegt (golf a4 — in ng1 stond `updateNodelabel` na elke
        //`addCyNode`, `addQuantity`, naamswijziging en zero-toggle apart).
        cy.on('add', 'node', event => this.zetNodelabel(event.target as NodeSingular));
        cy.on('add', 'node', verversNodes);
        cy.on('remove', 'node', verversNodes);

        /**
         * Node-hover: houd bij boven welke node de muis zweeft, zodat de
         * hover-overlays (uitleg, later feedback) reactief kunnen tonen/verbergen.
         *
         * Tijdens pannen/zoomen schuiven nodes onder de cursor door, waardoor
         * cytoscape een stroom `mouseover`/`mouseout`-events afvuurt. Om die churn
         * (op grote modellen merkbaar) te vermijden onderdrukken we hover zolang de
         * viewport beweegt, plus een korte cooldown na de laatste viewport-wijziging.
         */
        const blokkeerHoverTijdensViewport = (): void => {
            this.gehoverdeNodeId.set(null);
            this.timers.set('hoverblokkade', () => undefined, 150);
        };
        cy.on('viewport', blokkeerHoverTijdensViewport);

        cy.on('mouseover', 'node', (event: EventObject) => {
            if (this.timers.check('hoverblokkade')) return; // viewport beweegt (nog); geen hover
            this.gehoverdeNodeId.set((event.target as NodeSingular).id());
        });
        cy.on('mouseout', 'node', (event: EventObject) => {
            if (this.gehoverdeNodeId() === (event.target as NodeSingular).id()) {
                this.gehoverdeNodeId.set(null);
            }
        });

        /**
         * Tap op node: eerst de feedback-aanwijzing intrekken, dan de add-actie. Die volgorde houdt het
         * intrekken vóór een eventuele `create` op de lijn, zoals toen het nog via AngularJS liep.
         * Een tap op het label (buiten een add-actie) vraagt het naamveld van die node; elke andere tap
         * trekt dat verzoek in.
         */
        cy.on('tap', 'node', (event: EventObject) => {
            if (this.fullReadonly()) return;
            const node = event.target as NodeSingular;
            this.labelVerzoekId.set(!this.addActie() && this.isLabelKlik(node, event.position) ? node.id() : null);
            this.resetFeedback();
            this.checkAddActie(node);
        });

        /**
         * Tap op een relatie: de feedback-aanwijzing intrekken. De debuglog van de normmapping die hier in
         * AngularJS hing, staat op verzoek uit (besluit gebruiker, golf 4d); de methode bestaat nog.
         */
        cy.on('tap', 'edge', (_event: EventObject) => {
            if (this.fullReadonly()) return;
            //this.normService.logMapping((_event.target as EdgeSingular).id());
            this.resetFeedback();
        });

        /**
         * Een relatie gaat weg: zijn edgehandles gaan mee.
         */
        cy.on('remove', 'edge', (event: EventObject) => {
            this.dlModel.checkEdgeHandleOnDeleteEdge(event.target as EdgeSingular);
        });

        /**
         * Het einde van een sleep, gemeld door de automove van de base-directive.
         */
        cy.on('endAutomove', (_event: EventObject, automove?: Automove) => this.naSlepen(automove));

        /**
         * Tap op het canvas: is er een add-actie, dan komt het element hier te staan.
         */
        cy.on('tap', (event: EventObject) => {
            if (event.target !== cy || this.fullReadonly()) return;

            const p = event.position;
            let node: NodeSingular | null = null;

            // Check of dit toch niet in de bb van een node valt (klik op whitespace in label, bijvoorbeeld)
            cy.nodes().forEach((ele) => {
                const box = ele.boundingBox();
                if (box.x1 < p.x && box.x2 > p.x && box.y1 < p.y && box.y2 > p.y) {
                    node = ele;
                    return false; // break
                }
                return undefined;
            });

            if (node) {
                // Als het een node was (via BB), behandelen we het als een tik op die node.
                // Dit emuleert de 'node.emit(tap)' logica uit de oude controller.
                this.resetFeedback();
                this.checkAddActie(node);
                return;
            }
            this.resetFeedback();
            if (!this.addActie()) {
                return;
            }
            if (this.inAddRelation()) {
                this.annuleerAddActie(); //klik in het niets: de relatie gaat niet door
                return;
            }
            this.plaatsAddActie({x: p.x, y: p.y});
        });
    }

    /**
     * Ververst de nodes-lijst van de overlaylaag, en meldt het aantal aan de `GlobalModelcanvasService`:
     * de simulatieknoppen lezen daar of het model leeg is (golf 4d).
     */
    private zetNodes(cy: Core): void {
        const nodes = cy.nodes().toArray();
        this.nodes.set(nodes);
        this.globalModelcanvas.meldAantalNodes(nodes.length);
    }

    /**
     * Een tik op het canvas sluit de popupteksten van de feedbackbalk en trekt de aanwijzing van een
     * fout, suggestie of simfeedback-lus in — voor iedereen, want het intrekken reist mee.
     */
    private resetFeedback(): void {
        this.feedbackbalk.sluitPopupteksten();
        this.feedbackSelectie.wisSelectieDoorKlik();
    }

    /**
     * Brengt het model terug in beeld nadat het in zijn geheel verschoven is (snapNorm). De volgorde telt,
     * net als bij een nieuw model in `tekenDocument`: eerst centreren, want de "past het nog"-check van
     * `fitToMaxZoom` rekent met de huidige uitsnede. Zonder dat centreren zou een model dat na het
     * schuiven buiten beeld ligt óf altijd gefit worden, óf — als de check hem ruim genoeg vindt —
     * helemaal buiten beeld blijven staan (testronde 4d, test 13).
     */
    private brengBeeldOpMaat(): void {
        this.cy?.center();
        this.fitToMaxZoom(3, true); // alleen als het zo niet past
    }

    /**
     * Het einde van een sleep. De edgehandles gaan eerst naar hun beste plek — ook bij een volger, zodat
     * ze goed staan als het antwoord van de leider uitblijft — en daarna gaan de posities als actie naar
     * de baas: bij leider en solo zet die ze (idempotent) en slaat op, een volger vraagt het de leider.
     * Edgehandles reizen niet mee: hun ids zijn per client, elke kant zet ze zelf. Bleef er dan niets
     * over, dan is er niets te melden.
     * @param automove de gesleepte node en wat er meebewoog
     */
    private naSlepen(automove?: Automove): void {
        this.dlModel.repositionEdgeHandles();
        if (!automove?.mainnode) {
            return;
        }
        const posities = [automove.mainnode, ...((automove.movegroup?.toArray() ?? []) as NodeSingular[])]
            .filter(node => node.data('type') !== 'edgeHandle')
            .map(node => ({id: node.id(), pos: {...node.position()}}));
        if (posities.length) {
            this.samenwerkprotocol.doe('positie', {posities});
        }
    }

    /**
     * Onderdrukt de icoon-animaties tot de bijbehorende `laatAnimatiesLos`; zie `animatielock`.
     */
    private blokkeerAnimaties(): void {
        this.animatielock++;
    }

    /**
     * Heft één animatieblokkade op; zie `animatielock`.
     */
    private laatAnimatiesLos(): void {
        if (this.animatielock > 0) {
            this.animatielock--;
        }
    }

    /**
     * Wist de selectie en breekt de lopende add-actie af, omdat het canvas leeg gaat.
     */
    private resetInteractie(): void {
        this.wisSelectie();
    }

    /**
     * Zet het canvaslabel van een node: de nodenaam als het elementtype er een heeft en de naam
     * niet onderdrukt is (`skipName`, de zero van een quantity space), anders leeg. Speciale namen
     * (`___…`) worden vertaald. Na het tekenen van een document voor alle modelnodes in één keer,
     * in plaats van per node vanuit de deserialisatie.
     */
    private zetNodelabel(node: NodeSingular): void {
        this.dlModel.zetNodelabel(node);
    }

}
